import React, { useState, useEffect } from 'react';
import { Alert, AlertTitle, Button, Box, Typography, Tooltip, Chip } from '@mui/material';
import WarningIcon from '@mui/icons-material/Warning';
import VerifiedIcon from '@mui/icons-material/Verified';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import processNarrativeService from '../../services/ProcessNarrativeService';

/**
 * Banner component to display certification status of a process narrative
 * Shows warning when certification is overdue (> 90 days)
 * Minimalist design when certified and up-to-date
 */
const CertificationBanner = ({ appId, onCertify }) => {
    const [certStatus, setCertStatus] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchCertificationStatus = async () => {
            try {
                setLoading(true);
                const status = await processNarrativeService.checkCertificationStatus(appId);
                setCertStatus(status);
            } catch (error) {
                console.error("Error fetching certification status:", error);
            } finally {
                setLoading(false);
            }
        };

        if (appId) {
            fetchCertificationStatus();
        }
    }, [appId]);

    if (loading) {
        return null; // Don't show anything while loading
    }

    // Don't render if we don't have status data
    if (!certStatus) {
        return null;
    }

    const { lastCertified, needsCertification, daysSinceLastCertification } = certStatus;

    // Format date for display
    const formatDate = (dateString) => {
        if (!dateString) return 'Never';
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', { 
            year: 'numeric', 
            month: 'long', 
            day: 'numeric' 
        });
    };

    // If certified and up-to-date (less than 90 days), show minimalist banner
    if (!needsCertification) {
        return (
            <Box sx={{ 
                mb: 2, 
                mt: 1,
                display: 'flex', 
                justifyContent: 'flex-end', 
                alignItems: 'center',
                gap: 1
            }}>
                <Chip
                    icon={<VerifiedIcon sx={{ fontSize: '1rem !important' }} />}
                    label={`Certified ${daysSinceLastCertification} days ago`}
                    color="success"
                    size="small"
                    variant="outlined"
                    sx={{ 
                        height: 28, 
                        '& .MuiChip-label': { 
                            fontSize: '0.75rem',
                            fontWeight: 500
                        }
                    }}
                />
                <Button 
                    color="success" 
                    size="small" 
                    variant="outlined"
                    onClick={onCertify}
                    sx={{ 
                        height: 28, 
                        textTransform: 'none', 
                        fontSize: '0.75rem',
                        minWidth: 'auto',
                        px: 1.5
                    }}
                >
                    Re-Certify
                </Button>
            </Box>
        );
    }

    // If certification is needed (> 90 days), show full warning banner
    return (
        <Box sx={{ mb: 3 }}>
            <Alert 
                severity="warning" 
                icon={<WarningIcon />}
                action={
                    <Button 
                        color="warning" 
                        size="small" 
                        variant="outlined"
                        onClick={onCertify}
                        sx={{ ml: 1 }}
                    >
                        Certify Now
                    </Button>
                }
                sx={{ 
                    borderLeft: '4px solid #ed6c02',
                    '& .MuiAlert-icon': {
                        color: '#ed6c02',
                        fontSize: '1.25rem',
                    }
                }}
            >
                <AlertTitle sx={{ fontWeight: 600 }}>Certification Required</AlertTitle>
                <Typography variant="body2">
                    This process narrative has not been certified in {daysSinceLastCertification || 'over 90'} days. 
                    To maintain compliance, processes should be reviewed and certified quarterly.
                </Typography>
                
                <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                    <CalendarTodayIcon sx={{ fontSize: '0.9rem', mr: 0.5, color: 'text.secondary' }} />
                    <Typography variant="body2" color="text.secondary">
                        Last certified: {formatDate(lastCertified)}
                    </Typography>
                </Box>
            </Alert>
        </Box>
    );
};

export default CertificationBanner; 